import { Fix64 } from "../maths/fix64";

/**
 * The four enemy abilities added in M21 (docs/ENEMIES_V2.md), parsed from `enemies.json` beside the archetype's other
 * fields. Every field defaults to "no ability", so an archetype that names none of them behaves as it always did.
 */
export interface EnemyAbilities {
  /** Spellward: share of magic damage it takes (1 = all of it; 0.3 = a 70 % magic resist). */
  readonly magicTaken: Fix64;
  /** Never wears an elite affix (the Spellward: its runes are its trait — a Warded Spellward would shrug off nearly everything). */
  readonly noAffix: boolean;
  /** Summoner: the archetype it calls (−1 = none), how many per cast, the most of its own alive at once. */
  readonly summonEnemy: number;
  readonly summonCount: number;
  readonly summonCap: number;
  /** Summoner: ticks from spawn to the first cast, between casts, and the cast itself (it stands still, a telegraph). */
  readonly summonFirstDelayTicks: number;
  readonly summonIntervalTicks: number;
  readonly summonCastTicks: number;
  /** Summoner: road cells between the imps it calls (they appear behind it, toward the portal). */
  readonly summonSpacing: Fix64;
  /** Burrower: road cells it travels underground per dive (0 = never burrows), its speed there, dives per life. */
  readonly burrowDistance: Fix64;
  readonly burrowSpeedMult: Fix64;
  readonly burrowMax: number;
  /** Burrower: ticks digging in and climbing out (standing still, still targetable), and the cooldown after surfacing. */
  readonly burrowDigTicks: number;
  readonly burrowSurfaceTicks: number;
  readonly burrowCooldownTicks: number;
  /** Stunner: the shockwave's radius (0 = none) and how long it disables the towers it reaches. */
  readonly stompRadius: Fix64;
  readonly stompDisableTicks: number;
  /** Stunner: ticks from spawn to the first stomp, between stomps, and the wind-up (it stands still, a telegraph). */
  readonly stompFirstDelayTicks: number;
  readonly stompIntervalTicks: number;
  readonly stompWindupTicks: number;
}

export const NO_ABILITIES: EnemyAbilities = {
  magicTaken: Fix64.One, noAffix: false, summonEnemy: -1, summonCount: 0, summonCap: 0, summonFirstDelayTicks: 0, summonIntervalTicks: 0, summonCastTicks: 0, summonSpacing: Fix64.Zero,
  burrowDistance: Fix64.Zero, burrowSpeedMult: Fix64.One, burrowMax: 0, burrowDigTicks: 0, burrowSurfaceTicks: 0, burrowCooldownTicks: 0,
  stompRadius: Fix64.Zero, stompDisableTicks: 0, stompFirstDelayTicks: 0, stompIntervalTicks: 0, stompWindupTicks: 0,
};

/** Whether an archetype has any of the timed abilities (its enemies carry ability state in the hash). */
export function hasTimedAbility(a: EnemyAbilities): boolean {
  return a.summonEnemy >= 0 || a.burrowDistance.isPositive() || a.stompRadius.isPositive();
}

/** JSON parsing boundary (CLAUDE.md: no `any` outside it). */
type Json = any;

function opt(o: Json, key: string, fallback: Json): Json { return o !== null && typeof o === "object" && key in o ? o[key] : fallback; }
function fix(v: Json): Fix64 { return Fix64.parse(String(v)); }
function int(v: Json, where: string): number {
  if (typeof v !== "number" || !Number.isInteger(v) || v < 0) throw new Error(`${where}: expected a non-negative integer`);
  return v;
}

/** Parses one archetype's abilities; `ids` are every enemy id in file order (the summoned archetype is named by id). */
export function parseEnemyAbilities(e: Json, ids: readonly string[]): EnemyAbilities {
  const id = String(opt(e, "id", "?"));
  const n = (key: string) => int(opt(e, key, 0), `enemy ${id} ${key}`);
  const summonId = opt(e, "summonEnemy", null);
  const summonEnemy = summonId === null ? -1 : ids.indexOf(String(summonId));
  if (summonId !== null && summonEnemy < 0) throw new Error(`enemy ${id}: unknown summonEnemy '${summonId}'`);
  const out: EnemyAbilities = {
    magicTaken: fix(opt(e, "magicTaken", 1)), noAffix: opt(e, "noAffix", false) === true,
    summonEnemy, summonCount: n("summonCount"), summonCap: n("summonCap"),
    summonFirstDelayTicks: n("summonFirstDelayTicks"), summonIntervalTicks: n("summonIntervalTicks"), summonCastTicks: n("summonCastTicks"),
    summonSpacing: fix(opt(e, "summonSpacing", 0)),
    burrowDistance: fix(opt(e, "burrowDistance", 0)), burrowSpeedMult: fix(opt(e, "burrowSpeedMult", 1)), burrowMax: n("burrowMax"),
    burrowDigTicks: n("burrowDigTicks"), burrowSurfaceTicks: n("burrowSurfaceTicks"), burrowCooldownTicks: n("burrowCooldownTicks"),
    stompRadius: fix(opt(e, "stompRadius", 0)), stompDisableTicks: n("stompDisableTicks"),
    stompFirstDelayTicks: n("stompFirstDelayTicks"), stompIntervalTicks: n("stompIntervalTicks"), stompWindupTicks: n("stompWindupTicks"),
  };
  if (out.summonEnemy >= 0 && (out.summonCount <= 0 || out.summonCap <= 0 || out.summonIntervalTicks <= 0)) throw new Error(`enemy ${id}: a summoner needs summonCount, summonCap and summonIntervalTicks`);
  if (out.stompRadius.isPositive() && (out.stompDisableTicks <= 0 || out.stompIntervalTicks <= 0)) throw new Error(`enemy ${id}: a stunner needs stompDisableTicks and stompIntervalTicks`);
  if (out.burrowDistance.isPositive() && out.burrowMax <= 0) throw new Error(`enemy ${id}: a burrower needs burrowMax`);
  return out;
}
